import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';

@Injectable({ providedIn: 'root' })
export class JumpService extends AutomationDisplayService {
  constructor(protected readonly translateService: TranslateService) {
    super();
  }

  /** Gets text for the activity column in the table. */
  getActivityColumn(
    _namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): ActivityTableEntry {
    if (activity?.error) {
      return this.tableEntry(
        this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTIVITY.FAILURE'),
        this.emptyBody(),
      );
    }

    const nextStepId = activity?.nextWorkflowStep?.id;
    const executedStep = activity?.workflowStep;
    let displayContent$ = of(this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.JUMP.ACTIVITY.NO_BRANCH'));

    if (executedStep.choices?.defaultNextStep !== nextStepId) {
      const stepData = activity?.workflowStep?.data ? JSON.parse(activity.workflowStep.data) : {};
      const stepName = this.getStepNameFromId(stepData.step_to_jump_to, automation);
      displayContent$ = this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.JUMP.ACTIVITY.YES_BRANCH', {
        name: stepName,
      });
    }

    // Show user-friendly text if the step proceeded.
    return this.tableEntry(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.RATE_FILTER.ACTION.TITLE'),
      displayContent$,
    );
  }

  /** The text in an automation node (i.e. the nodes in the graph). */
  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
    automation: AutomationInterface,
  ): Observable<NodeData> {
    const data = step?.data ? JSON.parse(step.data) : {};
    const stepName = this.getStepNameFromId(data.step_to_jump_to, automation);
    return this.nodeDisplayData(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.JUMP.ACTION.NODE_DATA', {
        name: stepName,
        maximumJumps: data.max_number_of_jumps,
      }),
      this.noValidationErrorsDetected(),
    );
  }

  getStepNameFromId(stepId: string, automation: AutomationInterface): string {
    const step = automation?.workflow?.find((s) => s.id === stepId);
    if (!step) {
      return this.translateService.instant('AUTOMATIONS.COMMON.UNKNOWN_STEP');
    }
    return step.name || this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.JUMP.UNTITLED_STEP');
  }
}
